<script setup>
import {ref} from 'vue'
import axios from 'axios'
import {useRoute} from 'vue-router'
import {useI18n} from '@/i18n'

const route = useRoute()
const {t} = useI18n()
const props = defineProps({context: {type: String, default: ''}})
const form = ref({name: '', email: '', phone: '', country: '', projectType: '', size: '', message: ''})
const status = ref('')
const statusType = ref('')
const submitting = ref(false)

async function submitInquiry() {
  if (submitting.value) return
  submitting.value = true
  status.value = ''
  statusType.value = ''

  const details = [
    `Page context: ${props.context || route.path}`,
    `Project type: ${form.value.projectType}`,
    `Project country: ${form.value.country}`,
    `Approximate size: ${form.value.size || '-'}`,
    `Requirements: ${form.value.message}`
  ].join('\n')

  try {
    await axios.post('/api/mail', {
      name: form.value.name,
      email: form.value.email,
      phone: form.value.phone,
      contents: details,
      client_path: route.path,
      client_ip: '',
      client_country: form.value.country
    })
    status.value = t('marketing.home.inquiry.sent')
    statusType.value = 'success'
    form.value = {name: '', email: '', phone: '', country: '', projectType: '', size: '', message: ''}
  } catch (error) {
    status.value = t('marketing.home.inquiry.failed')
    statusType.value = 'error'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <form class="mkt-inquiry-form" @submit.prevent="submitInquiry">
    <div class="mkt-form-grid">
      <label class="mkt-form-field">
        <span>{{ t('marketing.home.inquiry.labels.name') }} *</span>
        <input v-model.trim="form.name" type="text" name="name" autocomplete="name" required>
      </label>
      <label class="mkt-form-field">
        <span>{{ t('marketing.home.inquiry.labels.email') }} *</span>
        <input v-model.trim="form.email" type="email" name="email" autocomplete="email" required>
      </label>
      <label class="mkt-form-field">
        <span>{{ t('marketing.home.inquiry.labels.phone') }}</span>
        <input v-model.trim="form.phone" type="tel" name="phone" autocomplete="tel">
      </label>
      <label class="mkt-form-field">
        <span>{{ t('marketing.home.inquiry.labels.country') }} *</span>
        <input v-model.trim="form.country" type="text" name="country" autocomplete="country-name" required>
      </label>
      <label class="mkt-form-field">
        <span>{{ t('marketing.home.inquiry.labels.projectType') }} *</span>
        <select v-model="form.projectType" name="project-type" required>
          <option value="" disabled></option>
          <option v-for="option in t('marketing.home.inquiry.options')" :key="option" :value="option">{{ option }}</option>
        </select>
      </label>
      <label class="mkt-form-field">
        <span>{{ t('marketing.home.inquiry.labels.size') }}</span>
        <input v-model.trim="form.size" type="text" name="size" placeholder="L × W × H">
      </label>
      <label class="mkt-form-field mkt-form-field-wide">
        <span>{{ t('marketing.home.inquiry.labels.message') }} *</span>
        <textarea v-model.trim="form.message" name="message" rows="5"
                  :placeholder="t('marketing.home.inquiry.placeholders.message')" required></textarea>
      </label>
    </div>
    <div class="mkt-form-actions">
      <button class="mkt-button mkt-button-primary" type="submit" :disabled="submitting">
        <span>{{ submitting ? t('marketing.home.inquiry.sending') : t('marketing.home.inquiry.submit') }}</span>
        <i class="mdi mdi-arrow-right" aria-hidden="true"></i>
      </button>
      <p v-if="status" class="mkt-form-status" :class="`is-${statusType}`" role="status">{{ status }}</p>
    </div>
  </form>
</template>
